import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { numeroPorExtenso } from '@/utils/numeroPorExtenso'

import PasswordConfirmationFields from '@/components/form/input/password-confirmation-fields'
import { getLabelRegimeTributario } from '@/components/form/shared/produtos-selector'
import { Card, CardContent } from '@/components/ui/card'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import * as z from 'zod'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { BancoSelect } from '../../../components/form/banco-select'
import { ClientRegimeFormData, ClientRegimeFormDataCpf } from '@/types/client'
import { produtosLabelsTypeContract } from '@/features/products/utils'
import { TypeContrato } from '@/features/agreement/utils/enum'

const PF_CONTRACT_TYPE = TypeContrato.PESSOA_FISICA
const MIN_COMISSAO = 20

interface Banco {
  ispb: string
  name: string
  code: string
}

interface ClientRegimeFormProps {
  requiresPassword?: boolean
  clientData?: {
    name: string
    email: string
    cpf: string
    whatsapp: string
  } | null
  contadorData?: {
    name: string
    email: string
    whatsapp: string
  }
  bancos: Banco[]
  initialData?: Partial<ClientRegimeFormData>
  onSubmit: (data: ClientRegimeFormData | ClientRegimeFormDataCpf) => Promise<void>
  isClienteCriado: boolean
  createStatusClient: string
  onEditClick: () => void
  isMinutaEnviada: boolean
  onAssinarContrato: () => Promise<void>
  onAssinarContratoADistancia: () => void
}

type FormSchema = ClientRegimeFormData & { confirmarSenha?: string }

export default function ClientRegimeFormPF({
  requiresPassword = true,
  clientData,
  contadorData,
  initialData,
  onSubmit,
  isMinutaEnviada,
  createStatusClient,
  // onEnviarMinuta,
  onAssinarContrato,
  onAssinarContratoADistancia
}: ClientRegimeFormProps) {
  const schema = requiresPassword
    ? z
        .object({
          regimeTributario: z.string().min(1, { message: 'Selecione um regime tributário' }),
          produtosSelecionados: z.array(z.string()).min(1, { message: 'Selecione pelo menos um produto' }),
          comissao: z
            .string()
            .min(1, { message: 'Informe a comissão' })
            .refine(value => !isNaN(Number(value)) && Number(value) >= MIN_COMISSAO, {
              message: `A comissão não pode ser menor que ${MIN_COMISSAO}%.`
            }),
          comissaoEscrita: z.string(),
          senha: z
            .string()
            .min(6, { message: 'A senha deve ter pelo menos 6 caracteres' })
            .refine(senha => /[a-zA-Z]/.test(senha) && /[0-9]/.test(senha) && senha.length >= 6, {
              message: 'A senha deve conter uma letra maiúscula, minúscula, número, caractere especial e ter 12+ caracteres.'
            }),
          confirmarSenha: z.string().min(1, { message: 'Confirme a senha digitada' }),
          banco: z.string().optional(),
          agencia: z.string().optional(),
          conta: z.string().optional(),
          concordo: z.boolean()
        })
        .refine(data => data.senha === data.confirmarSenha, {
          message: 'As senhas digitadas não são idênticas.',
          path: ['confirmarSenha']
        })
    : z.object({
        regimeTributario: z.string().min(1, { message: 'Selecione um regime tributário' }),
        produtosSelecionados: z.array(z.string()).min(1, { message: 'Selecione pelo menos um produto' }),
        comissao: z
          .string()
          .min(1, { message: 'Informe a comissão' })
          .refine(value => !isNaN(Number(value)) && Number(value) >= MIN_COMISSAO, {
            message: `A comissão não pode ser menor que ${MIN_COMISSAO}%.`
          }),
        comissaoEscrita: z.string(),
        senha: z.string().optional(),
        confirmarSenha: z.string().optional(),
        banco: z.string().optional(),
        agencia: z.string().optional(),
        conta: z.string().optional(),
        concordo: z.boolean()
      })

  const form = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...initialData,
      regimeTributario: initialData?.regimeTributario ?? undefined, // Ensure no null values
      produtosSelecionados: initialData?.produtosSelecionados || [],
      senha: initialData?.senha || '',
      confirmarSenha: initialData?.senha || '',
      concordo: initialData?.concordo || false
    },
    shouldFocusError: true
  })

  // Update comissaoEscrita when comissao changes
  const comissaoValue = form.watch('comissao')
  useEffect(() => {
    if (comissaoValue && !isNaN(Number(comissaoValue))) {
      form.setValue('comissaoEscrita', numeroPorExtenso(Number(comissaoValue)))
    } else {
      form.setValue('comissaoEscrita', '')
    }
  }, [comissaoValue, form])

  useEffect(() => {
    form.setValue('regimeTributario', PF_CONTRACT_TYPE, { shouldValidate: true })
    form.setValue('produtosSelecionados', [PF_CONTRACT_TYPE], { shouldValidate: true })
  }, [form])

  return (
    <Card className="mt-4 pt-4">
      <CardContent className="space-y-6">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(({ confirmarSenha, ...data }) => onSubmit(data))}
            className="space-y-6"
            autoComplete="off"
          >
            <div className="rounded-lg border bg-muted/40 p-4 text-sm">
              <p className="font-medium">Regime tributário</p>
              <p className="text-muted-foreground">{getLabelRegimeTributario(PF_CONTRACT_TYPE)}</p>
            </div>

            <div className="rounded-lg border bg-muted/40 p-4 text-sm">
              <p className="font-medium">Produto</p>
              <p className="text-muted-foreground">{produtosLabelsTypeContract[PF_CONTRACT_TYPE]}</p>
            </div>
            {/* Comissão */}
            <FormField
              control={form.control}
              name="comissao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comissão (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={MIN_COMISSAO}
                      max={100}
                      step={1} // passo inteiro
                      placeholder="Digite o valor da comissão"
                      {...field}
                      onChange={e => {
                        const raw = e.target.value
                        if (raw === '') {
                          field.onChange(raw)
                          return
                        }
                        // só números inteiros de 0 a 100; mínimo é validado no submit
                        if (/^\d+$/.test(raw) && Number(raw) >= 0 && Number(raw) <= 100) {
                          field.onChange(raw)
                        }
                      }}
                      className="bg-input text-foreground"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Comissão por extenso */}
            <FormField
              control={form.control}
              name="comissaoEscrita"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comissão por extenso</FormLabel>
                  <div className="p-2 bg-muted rounded-md">
                    <span className="font-medium">{field.value || 'Aguardando valor da comissão...'}</span>
                  </div>
                </FormItem>
              )}
            />
            {requiresPassword && (
              <PasswordConfirmationFields
                form={form}
                passwordName="senha"
                confirmationName="confirmarSenha"
                passwordLabel="Senha para o cliente"
                passwordPlaceholder="Digite a senha"
                confirmationLabel="Confirme a senha do cliente"
                confirmationPlaceholder="Digite novamente a senha"
              />
            )}
            {/* Dados Bancários */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Dados Bancários</h3>

              <FormField
                control={form.control}
                name="banco"
                render={({ field }) => <BancoSelect {...field} error={form.formState.errors.banco?.message} />}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Agência */}
                <FormField
                  control={form.control}
                  name="agencia"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agência</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="Digite a agência"
                          {...field}
                          className="bg-input text-foreground"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Conta */}
                <FormField
                  control={form.control}
                  name="conta"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Conta</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="Digite a conta"
                          {...field}
                          className="bg-input text-foreground"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            {/* Resumo das Informações */}
            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <h3 className="text-lg font-semibold">Resumo das Informações</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p>
                    <strong>Regime Tributário:</strong>
                    <br />
                    {getLabelRegimeTributario(form.watch('regimeTributario') as TypeContrato)}
                  </p>
                  <p>
                    <strong>Comissão:</strong>
                    <br />
                    {form.watch('comissao') ? `${form.watch('comissao')}%` : 'Não informada'}
                  </p>
                  <p>
                    <strong>Produtos Selecionados:</strong>
                    <br />
                    {form.watch('produtosSelecionados')?.length > 0
                      ? form
                          .watch('produtosSelecionados')
                          .map((p: string) => produtosLabelsTypeContract[p as TypeContrato])
                          .join(', ')
                      : 'Nenhum produto selecionado'}
                  </p>
                </div>

                <div className="space-y-2">
                  <p>
                    <strong>Cliente:</strong>
                    <br />
                    {clientData?.cpf || 'Não informado'}
                    <br />
                    <span className="text-muted-foreground">
                      {clientData?.email || 'Email não informado'}
                      <br />
                      {clientData?.whatsapp || 'WhatsApp não informado'}
                    </span>
                  </p>
                  <p>
                    <strong>Contador:</strong>
                    <br />
                    {contadorData?.name || 'Não informado'}
                    <br />
                    <span className="text-muted-foreground">
                      {contadorData?.email || 'Email não informado'}
                      <br />
                      {contadorData?.whatsapp || 'WhatsApp não informado'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            {/* Confirmação */}
            <FormField
              control={form.control}
              name="concordo"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormControl>
                    <div
                      className={`rounded-2xl border-2 p-4 transition-all ${
                        field.value
                          ? 'border-primary bg-primary/10 shadow-sm'
                          : 'border-dashed border-border bg-muted/40 hover:border-primary/50 hover:bg-primary/5'
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex-1 space-y-2">
                          <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            field.value
                              ? 'bg-primary text-primary-foreground hover:bg-primary/35'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {field.value ? 'Confirmado' : 'Pendente'}
                        </span>
                          <FormLabel className="cursor-pointer text-base font-semibold text-foreground">
                            Confirmo que as informações preenchidas acima estão corretas
                          </FormLabel>
                        </div>
                        <Button
                          type="button"
                          onClick={() => field.onChange(!field.value)}
                          className={`h-11 w-full rounded-xl text-sm font-semibold transition-all ${
                            field.value
                              ? 'bg-primary/35 text-primary-foreground hover:bg-primary/35'
                              : 'bg-red-600 text-white hover:bg-red-700'
                          }`}
                        >
                          {field.value ? 'Informações Confirmadas' : 'Clique para Confirmar'}
                        </Button>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Actions */}
            <div className="flex space-x-4">
              <Button
                type="submit"
                className="bg-primary hover:bg-primary-hover text-primary-foreground"
                disabled={!form.watch('concordo')}
              >
                {createStatusClient === 'pending' ? 'Enviando...' : 'Criar Cliente'}
              </Button>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                <Button
                  type="button"
                  onClick={onAssinarContratoADistancia}
                  disabled={!isMinutaEnviada}
                  className="flex-1 bg-primary hover:bg-primary-hover text-primary-foreground"
                >
                  Assinar Contrato a Distância
                </Button>
                <Button
                  type="button"
                  onClick={onAssinarContrato}
                  disabled={!isMinutaEnviada}
                  className="flex-1 bg-primary hover:bg-primary-hover text-primary-foreground"
                >
                  Assinar Contrato
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
